import type { Expediente } from '../expedientes/cliente-expedientes.js'
import { ErrorApi } from '../identidad/cliente-identidad.js'
import { notificar } from '../fundacion/alertas.js'
import {
  guardarPropia,
  listarRubrica,
  listarEvaluaciones,
  obtenerConsolidado,
  obtenerPropia,
  type CriterioRubrica,
  type MomentoEvaluacion,
} from './cliente-evaluaciones.js'

const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}
const textoError = (error: unknown) =>
  error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.'
let expediente: Expediente | null = null
let roles: string[] = []
let criterios: CriterioRubrica[] = []
let momento: MomentoEvaluacion = 'INTERMEDIA'
function informar(mensaje: string, tipo = 'success') {
  const alerta = elemento<HTMLElement>('#alerta-evaluaciones')
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}
function actorPuedeDiligenciar() {
  return (
    roles.some((rol) => ['ASESOR', 'TITULAR', 'ESTUDIANTE'].includes(rol)) &&
    expediente?.estado === 'ABIERTO'
  )
}
function nivel(nota: number) {
  return nota >= 4.5
    ? 'SUPERIOR'
    : nota >= 3.5
      ? 'ALTO'
      : nota >= 2.5
        ? 'BASICO'
        : nota >= 1.1
          ? 'BAJO'
          : 'NULO'
}
function claseNivel(valor: string | null): string {
  const clases = ['badge', 'nivel-desempeno']
  const normalizado = valor
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
  if (normalizado === 'SUPERIOR') return [...clases, 'bg-success'].join(' ')
  if (normalizado === 'ALTO') return [...clases, 'bg-info', 'text-dark'].join(' ')
  if (normalizado === 'BASICO') return [...clases, 'bg-warning', 'text-dark'].join(' ')
  if (normalizado === 'BAJO') return [...clases, 'bg-danger'].join(' ')
  if (normalizado === 'NULO') return [...clases, 'bg-secondary'].join(' ')
  return [...clases, 'bg-light', 'text-primary'].join(' ')
}
function actualizarNivel(fila: HTMLElement, criterio: CriterioRubrica) {
  const entrada = fila.querySelector<HTMLInputElement>('input[data-codigo]')
  const nivelElemento = fila.querySelector<HTMLElement>('[data-nivel]')
  const descriptor = fila.querySelector<HTMLElement>('[data-descriptor]')
  const valor = Number(entrada?.value)
  const clave = Number.isFinite(valor) ? nivel(valor) : null
  if (nivelElemento) {
    nivelElemento.textContent = clave ?? 'Pendiente'
    nivelElemento.className = claseNivel(clave)
  }
  if (descriptor) descriptor.textContent = clave ? (criterio.niveles[clave] ?? '') : ''
}
function reiniciarFormularioEvaluacion(): void {
  const formulario = elemento<HTMLFormElement>('#formulario-evaluacion')
  formulario.classList.remove('was-validated')
  formulario
    .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[data-codigo],textarea')
    .forEach((entrada) => {
      entrada.value = ''
    })
  criterios.forEach((criterio) => {
    const fila = formulario
      .querySelector<HTMLElement>(`[data-fila-criterio] [data-codigo="${criterio.codigo}"]`)
      ?.closest<HTMLElement>('[data-fila-criterio]')
    if (fila) actualizarNivel(fila, criterio)
  })
  formulario.querySelector<HTMLButtonElement>('button[type=submit]')!.disabled = false
}
async function cargarFormulario(): Promise<void> {
  if (!expediente || !actorPuedeDiligenciar()) return
  const formulario = elemento<HTMLFormElement>('#formulario-evaluacion')
  reiniciarFormularioEvaluacion()
  try {
    const evaluacion = await obtenerPropia(expediente.id, momento)
    if (!evaluacion) throw new Error('SIN_EVALUACION')
    for (const fila of evaluacion.calificaciones) {
      const entrada = formulario.querySelector<HTMLInputElement>(`[data-codigo="${fila.codigo}"]`)
      if (entrada) entrada.value = String(fila.nota)
      const contenedor = entrada?.closest<HTMLElement>('[data-fila-criterio]')
      if (contenedor) {
        const observacion = contenedor.querySelector<HTMLTextAreaElement>('[data-observacion]')
        const justificacion = contenedor.querySelector<HTMLTextAreaElement>('[data-justificacion]')
        if (observacion) observacion.value = fila.observacion ?? ''
        if (justificacion) justificacion.value = fila.justificacion_puntaje ?? ''
        const criterio = criterios.find((c) => c.codigo === fila.codigo)
        if (criterio) actualizarNivel(contenedor, criterio)
      }
    }
    formulario.querySelector<HTMLButtonElement>('button[type=submit]')!.disabled = true
    informar(`Evaluación ${momento.toLowerCase()} ya enviada.`, 'info')
  } catch {
    reiniciarFormularioEvaluacion()
    formulario.querySelector<HTMLButtonElement>('button[type=submit]')!.disabled = false
  }
}
async function cargarConsolidado(): Promise<void> {
  if (!expediente) return
  const consolidado = await obtenerConsolidado(expediente.id)
  const resultado = elemento<HTMLElement>('#resultado-consolidado')
  resultado.textContent = consolidado.disponible
    ? `Nota definitiva: ${consolidado.notaFinal?.toFixed(2)} — ${consolidado.nivelFinal}`
    : 'Consolidado pendiente de las seis evaluaciones.'
  const lista = elemento<HTMLElement>('#evaluaciones-realizadas')
  try {
    const evaluaciones = await listarEvaluaciones(expediente.id)
    lista.replaceChildren(
      ...evaluaciones.map((evaluacion) => {
        const tarjeta = document.createElement('details')
        tarjeta.className = 'border rounded p-2 mb-2'
        const resumen = document.createElement('summary')
        resumen.textContent = `${evaluacion.actor_nombre} · ${evaluacion.actor_tipo} · ${evaluacion.momento} · ${evaluacion.estado}`
        tarjeta.append(resumen)
        const tabla = document.createElement('table')
        tabla.className = 'table table-sm mt-2'
        const encabezado = document.createElement('thead')
        encabezado.innerHTML = '<tr><th>Criterio</th><th>Nota</th><th>Nivel Desempeño</th></tr>'
        const cuerpo = document.createElement('tbody')
        for (const fila of evaluacion.calificaciones) {
          const tr = document.createElement('tr')
          const criterio = criterios.find((item) => item.codigo === fila.codigo)
          const criterioCelda = document.createElement('td')
          criterioCelda.textContent =
            criterio?.descripcion ?? criterio?.desempenoEstudiante ?? fila.codigo
          const notaCelda = document.createElement('td')
          notaCelda.className = 'fw-bold'
          notaCelda.textContent = String(fila.nota)
          const nivelCelda = document.createElement('td')
          const nivelEtiqueta = document.createElement('span')
          nivelEtiqueta.className = claseNivel(fila.nivel)
          nivelEtiqueta.textContent = fila.nivel
          nivelCelda.append(nivelEtiqueta)
          tr.append(criterioCelda, notaCelda, nivelCelda)
          cuerpo.append(tr)
        }
        tabla.append(encabezado, cuerpo)
        tarjeta.append(tabla)
        return tarjeta
      }),
    )
  } catch {
    lista.replaceChildren()
  }
}
export async function mostrarEvaluaciones(nuevo: Expediente, nuevosRoles: string[]): Promise<void> {
  expediente = nuevo
  roles = nuevosRoles
  const esCoordinacion = nuevosRoles.some((rol) =>
    ['COORDINADOR_GENERAL', 'COORDINADOR_ASESORES'].includes(rol),
  )
  elemento('#filtro-momento-evaluacion').classList.toggle('d-none', esCoordinacion)
  if (esCoordinacion) {
    momento = 'INTERMEDIA'
    elemento<HTMLSelectElement>('#momento-evaluacion').value = momento
  }
  elemento('#panel-evaluaciones').classList.remove('d-none')
  criterios = (await listarRubrica()).criterios
  const tabla = elemento<HTMLElement>('#tabla-criterios-evaluacion')
  tabla.replaceChildren(
    ...criterios.map((criterio, indice) => {
      const fila = document.createElement('div')
      fila.className = 'row g-2 border-bottom py-3'
      fila.dataset.filaCriterio = 'true'
      const descripcion = document.createElement('div')
      descripcion.className = 'col-md-8'
      const encabezado = document.createElement('strong')
      encabezado.textContent = `Criterio ${indice + 1}`
      const desempeno = document.createElement('span')
      desempeno.textContent = criterio.desempenoEstudiante
      const proceso = document.createElement('small')
      proceso.className = 'text-muted'
      proceso.textContent = criterio.procesoPractica
      descripcion.append(
        encabezado,
        document.createElement('br'),
        desempeno,
        document.createElement('br'),
        proceso,
      )
      const nota = document.createElement('input')
      nota.className = 'form-control'
      nota.type = 'number'
      nota.min = '0'
      nota.max = '5'
      nota.step = '0.1'
      nota.required = true
      nota.dataset.codigo = criterio.codigo
      nota.setAttribute('aria-label', `Nota ${criterio.codigo}`)
      const celda = document.createElement('div')
      celda.className = 'col-md-4'
      const nivelVista = document.createElement('div')
      const nivelEtiqueta = document.createElement('span')
      nivelEtiqueta.className = claseNivel(null)
      nivelEtiqueta.dataset.nivel = 'true'
      nivelEtiqueta.textContent = 'Pendiente'
      const descriptor = document.createElement('div')
      descriptor.className = 'small text-muted mt-1'
      descriptor.dataset.descriptor = 'true'
      nivelVista.append(nivelEtiqueta, descriptor)
      const observacion = document.createElement('textarea')
      observacion.className = 'form-control form-control-sm mt-2'
      observacion.placeholder = 'Observación cualitativa (opcional)'
      observacion.dataset.observacion = 'true'
      const justificacion = document.createElement('textarea')
      justificacion.className = 'form-control form-control-sm mt-2'
      justificacion.placeholder = 'Justificación del puntaje (opcional)'
      justificacion.dataset.justificacion = 'true'
      nota.addEventListener('input', () => actualizarNivel(fila, criterio))
      celda.append(nota, nivelVista, observacion, justificacion)
      fila.append(descripcion, celda)
      return fila
    }),
  )
  elemento('#formulario-evaluacion').classList.toggle('d-none', !actorPuedeDiligenciar())
  await cargarFormulario()
  await cargarConsolidado()
  elemento('#panel-evaluaciones').scrollIntoView({ behavior: 'smooth', block: 'start' })
}
elemento<HTMLSelectElement>('#momento-evaluacion').addEventListener('change', (evento) => {
  momento = (evento.currentTarget as HTMLSelectElement).value as MomentoEvaluacion
  void cargarFormulario().catch((error: unknown) => informar(textoError(error), 'danger'))
})
elemento<HTMLFormElement>('#formulario-evaluacion').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  formulario.classList.add('was-validated')
  if (!formulario.checkValidity() || !expediente) return
  const notas = criterios.map((criterio) => ({
    codigo: criterio.codigo,
    nota: Number(
      formulario.querySelector<HTMLInputElement>(`[data-codigo="${criterio.codigo}"]`)!.value,
    ),
    observacion:
      formulario
        .querySelector<HTMLTextAreaElement>(`[data-codigo="${criterio.codigo}"]`)
        ?.closest('[data-fila-criterio]')
        ?.querySelector<HTMLTextAreaElement>('[data-observacion]')?.value || undefined,
    justificacionPuntaje:
      formulario
        .querySelector<HTMLInputElement>(`[data-codigo="${criterio.codigo}"]`)
        ?.closest('[data-fila-criterio]')
        ?.querySelector<HTMLTextAreaElement>('[data-justificacion]')?.value || undefined,
  }))
  void guardarPropia(expediente.id, momento, notas)
    .then(() => cargarFormulario())
    .then(() => cargarConsolidado())
    .then(() => informar('Evaluación enviada y auditada.'))
    .catch((error: unknown) => informar(textoError(error), 'danger'))
})
